import { useCallback, useMemo } from 'react';
import {
  pickBy,
  isNil,
  pick,
  complement,
  pipe,
  propEq,
  find,
  set,
  lensPath,
} from 'ramda';
import useQueryParameters from 'hooks/useQueryParameters';
import window from 'window';
import useReactRouter from 'use-react-router';
import qs from 'query-string';
import { CreditApplication, isOptInOrOptOut } from 'state/creditApplication';
import { useSelector } from 'react-redux';
import { selectTenant, Tenant } from 'state/tenant';
import { useDeferredAnalytics } from './useAnalytics';

export interface AnalyticsVariables {
  epId?: string;
  callId?: string;
  epPlacementId?: string;
  clientName?: string;
  pixelType?: string;
  urlPath?: string;
  epSessionId?: string;
  epMessageId?: string;
  location?: string;
  workflow?: string;
  embedded?: string;
  consumerSearchSuccessful?: string;
  isSufficientPrefill?: string;
  hasAccountAssure?: string;
  hasAuthBuyer?: string;
  productChoiceSelection?: string;
  coBrandOptIn?: string;
  coBrandOptOut?: string;
  pageName?: string;
  accountId?: string;
  applicationId?: string;
  divisionNumber?: string;
}

const evarMapping = {
  epId: 'eVar6',
  callId: 'eVar9',
  epPlacementId: 'eVar11',
  clientName: 'eVar12',
  urlPath: 'eVar13',
  epSessionId: 'eVar14',
  epMessageId: 'eVar15',
  location: 'eVar17',
  workflow: 'eVar18',
  embedded: 'eVar19',
  consumerSearchSuccessful: 'eVar20',
  isSufficientPrefill: 'eVar21',
  hasAccountAssure: 'eVar22',
  hasAuthBuyer: 'eVar23',
  productChoiceSelection: 'eVar24',
  coBrandOptIn: 'eVar26',
  coBrandOptOut: 'eVar27',
  pageName: undefined,
  pixelType: undefined,
  accountId: undefined,
  applicationId: undefined,
  divisionNumber: undefined,
} as const;

const mapAnalyticsVariables = {
  clientName: ['client', 'clientName'],
  pixelType: ['client', 'pixelType'],
  pageName: ['page', 'pageName'],
  isSufficientPrefill: ['application', 'isSufficientPrefill'],
  productChoiceSelection: ['application', 'productChoiceSelection'],
  consumerSearchSuccessful: ['application', 'consumerSearchSuccessful'],
  coBrandOptIn: ['application', 'coBrandOptIn'],
  coBrandOptOut: ['application', 'coBrandOptOut'],
  workflow: ['application', 'workflow'],
  hasAccountAssure: ['application', 'hasAccountAssure'],
  hasAuthBuyer: ['application', 'hasAuthBuyer'],
  epId: ['enhancedPresentment', 'epId'],
  callId: ['application', 'callId'],
  epPlacementId: ['enhancedPresentment', 'epPlacementId'],
  epSessionId: ['enhancedPresentment', 'epSessionId'],
  epMessageId: ['enhancedPresentment', 'epMessageId'],
  location: ['enhancedPresentment', 'location'],
  urlPath: ['enhancedPresentment', 'urlPath'],
  embedded: ['enhancedPresentment', 'embedded'],
  accountId: ['application', 'accountId'],
  applicationId: ['application', 'applicationId'],
  divisionNumber: ['client', 'divisionNumber'],
} as const;

export type AnalyticsVariablesHook = () => {
  setAnalyticsVariablesOnLoad: () => void;
  setAnalyticsVariablesByApplication: (application: CreditApplication) => void;
  setAnalyticsVariables: (variables: AnalyticsVariables) => void;
};

const findProductChoice = (tenant: Tenant, productChoiceKey: string | null) => {
  if (!tenant.enabledFeatures.productChoices || !productChoiceKey) {
    return null;
  }
  return find(
    propEq('key', productChoiceKey),
    tenant.enabledFeatures.productChoices,
  );
};

export const useAnalyticsVariables: AnalyticsVariablesHook = () => {
  const tenant = useSelector(selectTenant);
  const { workflow, embedded } = useQueryParameters();
  const {
    location: { search },
  } = useReactRouter();

  const query = useMemo(() => {
    const result = qs.parse(search, {
      parseBooleans: true,
    });
    result.embedded = embedded;
    result.workflow = workflow;

    return result;
  }, [search, embedded, workflow]);

  const setEvar = useDeferredAnalytics(
    useCallback(
      (key: keyof AnalyticsVariables, value: string | undefined): void => {
        const { s: siteCatalystAnalytics } = window;

        if (!siteCatalystAnalytics) {
          return;
        }

        const mapped = evarMapping[key];
        if (mapped) siteCatalystAnalytics[mapped] = value;
      },
      [],
    ),
  );

  const setAnalyticsVariable = useCallback(
    (key: keyof AnalyticsVariables, value: string | undefined): void => {
      // Data layer
      window.digitalData = set(
        lensPath(mapAnalyticsVariables[key].flat()),
        value,
        window.digitalData,
      );

      // Evars
      setEvar(key, value);
    },
    [setEvar],
  );

  const setAnalyticsVariables = useCallback(
    (variables: Partial<AnalyticsVariables>): void => {
      const existingKeys = Object.keys(variables);
      existingKeys.forEach((key, _) => {
        setAnalyticsVariable(
          key as keyof AnalyticsVariables,
          variables[key as keyof AnalyticsVariables],
        );
      });
    },
    [setAnalyticsVariable],
  );

  const setAnalyticsVariablesOnLoad = useCallback((): void => {
    const variables = pipe(
      pick([
        'epId',
        'epPlacementId',
        'clientName',
        'urlPath',
        'epSessionId',
        'epMessageId',
        'location',
        'workflow',
        'embedded',
      ]),
      pickBy(complement(isNil)),
    )(query) as Partial<AnalyticsVariables>;

    variables.consumerSearchSuccessful = 'false';
    variables.isSufficientPrefill = 'false';

    variables.divisionNumber = tenant.divisionNumber;
    if (tenant.enabledFeatures.pixelTracking) {
      variables.pixelType = tenant.enabledFeatures.pixelTracking.type;
    }

    if (tenant.divisionNumber) {
      setAnalyticsVariables({ divisionNumber: tenant.divisionNumber });
    }

    setAnalyticsVariables(variables);
  }, [
    query,
    tenant.divisionNumber,
    tenant.enabledFeatures.pixelTracking,
    setAnalyticsVariables,
  ]);

  const setAnalyticsVariablesByApplication = useCallback(
    (application: CreditApplication) => {
      if (tenant.enabledFeatures.accountAssure) {
        setAnalyticsVariables({
          hasAccountAssure: application.accountAssure ? 'true' : 'false',
        });
      }

      if (tenant.enabledFeatures.authBuyer) {
        setAnalyticsVariables({
          hasAuthBuyer: application.authBuyer ? 'true' : 'false',
        });
      }

      if (tenant.enabledFeatures.productChoices) {
        const choice = findProductChoice(tenant, application.productChoiceKey);
        if (choice)
          setAnalyticsVariables({ productChoiceSelection: choice.cardType });
      }

      if (tenant.enabledFeatures.optInOut) {
        const optInOrOut = isOptInOrOptOut(application, tenant);
        if (tenant.enabledFeatures.optInOut.type === 'optIn') {
          setAnalyticsVariables({
            coBrandOptIn: optInOrOut === 'optIn' ? 'true' : 'false',
          });
        } else {
          setAnalyticsVariables({
            coBrandOptOut: optInOrOut === 'optOut' ? 'true' : 'false',
          });
        }
      }
    },
    [setAnalyticsVariables, tenant],
  );

  return {
    setAnalyticsVariables,
    setAnalyticsVariablesOnLoad,
    setAnalyticsVariablesByApplication,
  };
};

export default useAnalyticsVariables;
